import {
  INemsisLookupArguments,
  INemsisLookupMetaArguments,
  INemsisProcedure,
  INemsisSnomedLookup,
  MoreThanOneNemsisCodeFoundError,
  NemsisCodeError,
  NemsisCodeNotFoundError,
  NullNemsisCodeFoundError,
} from '../components/procedure/store/procedure.types'
import Logger from '../services/logger.service'
import { getValueOrNull } from './object.util'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getNemsisCodeForProcedure = (
  metaArgs: INemsisLookupMetaArguments<any>
): { snomedId: number | null; code: string | number | null } => {
  const {
    nemsisCodeOptions,
    procedureState: { type: procedure },
  } = metaArgs

  if (!procedure) {
    return { snomedId: null, code: null }
  }

  if (!nemsisCodeOptions) {
    console.error('Client document for NEMSIS code options was not found')
    return { snomedId: null, code: null }
  }

  let options: INemsisSnomedLookup[] | null = null

  try {
    const action: string | null = getValueOrNull(metaArgs.actionField, metaArgs.procedureState)
    const filter1: string | null = getValueOrNull(metaArgs.filter1Field, metaArgs.procedureState)
    const filter2: string | null = getValueOrNull(metaArgs.filter2Field, metaArgs.procedureState)
    const filter3: string | null = getValueOrNull(metaArgs.filter3Field, metaArgs.procedureState)

    options = filterNemsisCodes(nemsisCodeOptions, {
      procedure,
      action,
      filter1,
      filter2,
      filter3,
    })
  } catch (e) {
    console.error('Error occurred while looking up filter values')
    console.error(e)
  }

  if (options == null || options.length === 0) {
    return { snomedId: null, code: null }
  }

  if (options.length > 1) {
    throw new MoreThanOneNemsisCodeFoundError()
  }

  if (!options?.[0]?.code) throw new NullNemsisCodeFoundError()
  return {
    code: options[0]?.code,
    snomedId: options[0]?.snomedId,
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getUpdatedProcedureStateWithNemsisCode = (metaArgs: INemsisLookupMetaArguments<any>) => {
  const { code, snomedId } = getNemsisCodeForProcedure(metaArgs)

  return {
    ...metaArgs.procedureState,
    nemsisCode: code,
    snomedId: metaArgs.includeSnomedId ? snomedId : null,
  }
}

export const filterNemsisCodes = (availableCodes: INemsisSnomedLookup[], filterArguments: INemsisLookupArguments) => {
  const { procedure, action, filter1, filter2, filter3 } = filterArguments

  const procedureCodes = availableCodes.filter((e: INemsisSnomedLookup) => e.procedure === procedure)

  // Try to find a matching NEMSIS code without 'Any Value'
  let codes = procedureCodes.filter(
    (e: INemsisSnomedLookup) =>
      matchProcedureField(e.action, action) &&
      matchProcedureField(e.filter1, filter1, true) &&
      matchProcedureField(e.filter2, filter2, true) &&
      matchProcedureField(e.filter3, filter3, true)
  )

  if (codes.length === 0) {
    // If no codes found using exact matches, re-run search with wildcard values enabled
    codes = procedureCodes.filter(
      (e: INemsisSnomedLookup) =>
        // e.action === action && // for intubation/digital, this line produces no results
        matchProcedureField(e.action, action, true) && // for intubation/digital, this line produces more than one result
        matchProcedureField(e.filter1, filter1, true) &&
        matchProcedureField(e.filter2, filter2, true) &&
        matchProcedureField(e.filter3, filter3, true)
    )
  }

  return codes
}

export const matchProcedureField = (
  fieldLookup: string | null,
  fieldValue: string | null,
  allowAny = false
): boolean => {
  if (fieldLookup == null && allowAny) {
    return true
  }

  if (allowAny && fieldLookup === 'Any Value') {
    return true
  }

  // If a procedure contains just 0 or a comma-delimited list including 0, also accept undefined or null
  if (fieldLookup === '0' && !fieldValue) {
    return true
  }

  if (fieldLookup && fieldLookup.includes(',') && fieldLookup.split(',').includes('0') && !fieldValue) {
    return true
  }

  // If the lookup value is not null or 'Any Value'
  // then the field value must match. It cannot match if
  // it is null.
  if (fieldValue == null) {
    return false
  }

  // Some procedures contain comma-delimited lists.
  // e.g. Intubation FIELD6: 0,1,2,3,4,5,6
  if (fieldLookup && fieldLookup.includes(',') && fieldLookup.split(',').includes(fieldValue)) {
    return true
  }

  return fieldLookup === fieldValue
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleNemsisCodeError = <T extends INemsisProcedure>(error: any, procedureState: T): T => {
  if (error instanceof NemsisCodeError) {
    if (error instanceof MoreThanOneNemsisCodeFoundError) {
      Logger.warn(`Multiple NEMSIS codes found for procedure ${procedureState.type}`)
    } else if (error instanceof NemsisCodeNotFoundError) {
      Logger.warn(`NEMSIS code not found for procedure ${procedureState.type}`)
    } else if (error instanceof NullNemsisCodeFoundError) {
      Logger.warn(`NEMSIS lookup returned a record with null 'code' field for procedure ${procedureState.type}`)
    }
  } else {
    const { message, stackTrace } = error
    Logger.error(`Unexpected error encountered while attempting to perform NEMSIS code lookup ${message} ${stackTrace}`)
  }
  return (procedureState = { ...procedureState, nemsisCode: null })
}
